import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { usePageMeta } from "@/lib/usePageMeta";
import { useLang } from "@/lib/i18n";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { HeroDevice } from "@/components/HeroDevice";

const EASE = [0.22, 1, 0.36, 1];

export default function Home() {
  const { t, to } = useLang();
  const h = t.home;
  usePageMeta(h.meta);

  return (
    <>
      <section className="relative overflow-hidden border-b border-line" data-testid="hero-section">
        <div className="grid-bg absolute inset-0" />
        <div className="container-x relative grid items-center gap-14 pb-20 pt-16 sm:pt-24 lg:grid-cols-12 lg:gap-8 lg:pb-32 lg:pt-28">
          <div className="lg:col-span-7">
            <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
              <span className="mr-3 inline-block h-1.5 w-1.5 rounded-full bg-coral align-middle" />
              {h.eyebrow}
            </motion.p>
            <MaskedLines
              as="h1"
              lines={["TubeOptimizer LLC"]}
              delay={0.15}
              className="mt-7 text-5xl font-extrabold tracking-[-0.03em] text-navy sm:text-6xl lg:text-7xl"
              data-testid="hero-title"
            />
            <MaskedLines
              as="p"
              lines={h.taglineLines}
              delay={0.35}
              className="mt-4 text-2xl font-medium tracking-tight text-brand sm:text-3xl lg:text-4xl"
              data-testid="hero-tagline"
            />
            <motion.p
              className="mt-8 max-w-lg text-base leading-relaxed text-muted md:text-lg"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
              data-testid="hero-description"
            >
              {h.description}
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            >
              <Link to={to("/apps")} className="btn-primary" data-testid="hero-cta-apps">
                {h.ctaApps} <ArrowRight size={16} />
              </Link>
              <Link to={to("/about")} className="btn-ghost" data-testid="hero-cta-about">
                {h.ctaAbout}
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <HeroDevice />
          </div>
        </div>
      </section>

      <Marquee />

      <section className="border-b border-line" data-testid="manifesto-section">
        <div className="container-x py-20 sm:py-32">
          <Reveal className="grid gap-6 md:grid-cols-12">
            <p className="eyebrow md:col-span-3">{h.approachEyebrow}</p>
            <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-navy sm:text-4xl md:col-span-9">{h.approachTitle}</h2>
          </Reveal>
          <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-line bg-line md:grid-cols-3">
            {h.chapters.map((c, i) => (
              <Reveal
                key={c.n}
                delay={i * 0.12}
                className="group relative bg-white p-8 transition-colors duration-500 hover:bg-cream sm:p-10"
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <span className="font-mono text-5xl font-medium tracking-tight text-coral sm:text-6xl">{c.n}</span>
                <h3 className="mt-10 text-xl font-bold tracking-tight text-navy">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted md:text-base">{c.text}</p>
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-coral transition-[width] duration-700 ease-out group-hover:w-full" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-cta-section">
        <div className="container-x py-20 sm:py-32">
          <Reveal className="relative overflow-hidden rounded-3xl bg-navy px-8 py-16 text-cream sm:px-14 sm:py-24">
            <div className="grain absolute inset-0" />
            <div className="relative grid items-end gap-10 md:grid-cols-12">
              <div className="md:col-span-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-coral">{h.ctaSection.eyebrow}</p>
                <h2 className="mt-5 max-w-xl text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">{h.ctaSection.title}</h2>
                <p className="mt-5 max-w-md text-base leading-relaxed text-cream/75">{h.ctaSection.text}</p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Link
                  to={to("/apps")}
                  className="inline-flex items-center gap-3 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-hover"
                  data-testid="home-cta-apps"
                >
                  {h.ctaSection.button} <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
